import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Tooltip, Checkbox, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, GetApp as ExportIcon, Search as SearchIcon, Clear as ClearIcon, Build as BuildIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import { fetchToolType, searchToolType, deleteToolType } from '../../store/reducers/toolTypeSlice';

const useStyles = makeStyles((theme) => ({ root: { padding: theme.spacing(3) }, header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(3) }, searchContainer: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(3), alignItems: 'center' }, tableContainer: { marginBottom: theme.spacing(2) }, statusChip: { minWidth: 80 }, loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 } }));

const ToolTypeList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, pagination, selectedIds, successMessage } = useSelector((s: RootState) => (s as any).toolType);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => { dispatch(fetchToolType()); }, [dispatch]);

  const handleSearch = () => { dispatch(searchToolType({ page: 1, pageSize: pagination.pageSize, search: searchTerm, active: activeFilter === 'all' ? undefined : activeFilter === 'true' } as any)); };
  const handleClearSearch = () => { setSearchTerm(''); setActiveFilter('all'); dispatch(fetchToolType({ page: 1, pageSize: pagination.pageSize } as any)); };

  const handlePageChange = (_: unknown, newPage: number) => { dispatch(fetchToolType({ page: newPage + 1, pageSize: pagination.pageSize, search: searchTerm || undefined, active: activeFilter === 'all' ? undefined : activeFilter === 'true' } as any)); };
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => { dispatch(fetchToolType({ page: 1, pageSize: parseInt(e.target.value, 10), search: searchTerm || undefined, active: activeFilter === 'all' ? undefined : activeFilter === 'true' } as any)); };

  const handleDelete = (id: number) => { if (window.confirm('Delete this Tool Type?')) dispatch(deleteToolType(id)); };

  if (loading && items.length === 0) return (<Box className={classes.loadingContainer}><CircularProgress /></Box>);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Tool Types</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/catalogs/tooltype/add')}>Add New</Button>
      </Box>
      {error && (<Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>)}
      {successMessage && (<Alert severity="success" style={{ marginBottom: 16 }}>{successMessage}</Alert>)}

      <Box className={classes.searchContainer}>
        <TextField label="Search" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} style={{ minWidth: 240 }} />
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as string)} label="Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>Clear</Button>
        <Button variant="outlined" startIcon={<ExportIcon />} disabled>Export CSV</Button>
      </Box>

      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: { Id?: number; Name: string; Description?: string; Active: boolean }) => (
                <TableRow key={item.Id} hover>
                  <TableCell><Box display="flex" alignItems="center"><BuildIcon style={{ marginRight: 8, fontSize: 20 }} />{item.Name}</Box></TableCell>
                  <TableCell>{item.Description || '-'}</TableCell>
                  <TableCell><Chip label={item.Active ? 'Active' : 'Inactive'} color={item.Active ? 'primary' : 'default'} size="small" /></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => navigate(`/catalogs/tooltype/edit/${item.Id}`)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="secondary" onClick={() => handleDelete(item.Id!)}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={pagination.total} rowsPerPage={pagination.pageSize} page={pagination.page - 1} onPageChange={handlePageChange} onRowsPerPageChange={handlePageSizeChange} />
      </Paper>
    </Box>
  );
};

export default ToolTypeList; 